import { useEffect } from 'react'

import { adminByIdQuery } from '@/entities/admins'
import { STRINGS } from '@/shared/config'
import { confirm } from '@/shared/lib/antd-utils'
import { hasFormChanged } from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import { HidingSpin } from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import { Button, Flex, Form, Input, Modal } from 'antd'
import { useGate, useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { FIELD_LENGTH } from './lib/constants'
import {
  getAdminCreateRequestByFormValues,
  getAdminUpdateRequestByFormValues,
} from './lib/get-admin-submit-request'
import {
  $isAdminFormModalOpened,
  $submittingAdmin,
  AdminFormGate,
  adminCreated,
  adminFormModalClosed,
  adminUpdated,
} from './model'

export { adminFormModalOpened } from './model'

const adminSchema = z.object({
  login: z
    .string()
    .min(FIELD_LENGTH.login.minLength, STRINGS.string_error_limit_min)
    .max(FIELD_LENGTH.login.maxLength, STRINGS.string_error_limit_max),
  password: z
    .string()
    .min(FIELD_LENGTH.password.minLength, STRINGS.string_error_limit_min)
    .max(FIELD_LENGTH.password.maxLength, STRINGS.string_error_limit_max),
})

export type AdminFormModel = z.infer<typeof adminSchema>

interface AdminFormProps {
  isModal?: boolean
  onSubmitted?: (adminId: string) => void
}

export const AdminForm = ({ isModal, onSubmitted }: AdminFormProps) => {
  const {
    data: adminData,
    pending: adminPending,
    error: adminError,
  } = useUnit(adminByIdQuery)
  const adminQueryStatus = useUnit(adminByIdQuery.$status)

  const createAdmin = useUnit(adminCreated)
  const updateAdmin = useUnit(adminUpdated)
  const submitting = useUnit($submittingAdmin)

  const editing = adminQueryStatus !== 'initial'
  const isModalOpened = useUnit($isAdminFormModalOpened)
  const closeFormModal = useUnit(adminFormModalClosed)

  const formik = useFormik<AdminFormModel>({
    enableReinitialize: true,
    initialValues: adminData
      ? {
          login: adminData.login || '',
          password: '',
        }
      : {
          login: '',
          password: '',
        },
    validationSchema: toFormikValidationSchema(adminSchema),
    validateOnMount: false,
    onSubmit: (values: AdminFormModel, { setSubmitting }) => {
      if (editing) {
        const adminSubmittedData = getAdminUpdateRequestByFormValues(values)
        updateAdmin(adminSubmittedData)
      } else {
        const adminSubmittedData = getAdminCreateRequestByFormValues(values)
        createAdmin(adminSubmittedData)
      }
      setSubmitting(false)
    },
  })

  useGate(AdminFormGate, {
    onSubmitted,
    isModal,
    resetForm: formik.resetForm,
  })

  const { isValid, isValidating, isSubmitting } = formik

  const isDirty = hasFormChanged(formik.values, formik.initialValues)

  useEffect(() => {
    if (isSubmitting && !isValid && !isValidating) {
      const errors = Object.values(formik.errors)
      if (errors.length > 0) {
        void showNotificationWarningFx({
          message: 'Ошибка валидации',
          description: errors.join(' '),
        })
      }
    }
  }, [isSubmitting, isValid, isValidating, formik.errors])

  useEffect(() => {
    if (isDirty) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault()
      }
      window.addEventListener('beforeunload', handler)
      return () => {
        window.removeEventListener('beforeunload', handler)
      }
    }
  }, [isDirty])

  const tryToClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: 'Данные не будут сохранены',
        content: 'Вы уверены, что хотите закрыть форму?',
        okText: 'Да, закрыть',
        cancelText: 'Отмена',
        centered: true,
      })
      if (!confirmed) return
    }
    formik.resetForm()
    closeFormModal()
  }

  const formControlButtonsDisabled =
    !hasFormChanged(formik.values, formik.initialValues) || submitting

  const formComponent = (
    <HidingSpin
      spinning={
        adminQueryStatus !== 'initial' &&
        (adminPending || (!adminData && !adminError))
      }
    >
      <Form.Item
        layout="vertical"
        label="Логин"
        required
        validateStatus={
          formik.touched.login && formik.errors.login ? 'error' : undefined
        }
      >
        <Input
          name="login"
          disabled={editing}
          showCount
          minLength={FIELD_LENGTH.login.minLength}
          maxLength={FIELD_LENGTH.login.maxLength}
          value={formik.values.login}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      </Form.Item>

      <Form.Item
        layout="vertical"
        label="Пароль"
        required={!editing}
        validateStatus={
          formik.touched.password && formik.errors.password
            ? 'error'
            : undefined
        }
      >
        <Input
          name="password"
          showCount
          minLength={FIELD_LENGTH.password.minLength}
          maxLength={FIELD_LENGTH.password.maxLength}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      </Form.Item>

      <Flex justify="flex-end" gap={10}>
        <PrimaryButton
          disabled={formControlButtonsDisabled}
          onClick={() => {
            formik.handleSubmit()
          }}
          loading={submitting}
        >
          {editing ? 'Сохранить' : 'Добавить'}
        </PrimaryButton>
        {!editing && (
          <Button
            disabled={formControlButtonsDisabled}
            onClick={() => {
              formik.resetForm()
            }}
          >
            Очистить
          </Button>
        )}
      </Flex>
    </HidingSpin>
  )

  return (
    <>
      {isModal ? (
        <Modal
          open={isModalOpened}
          onCancel={() => {
            void tryToClose()
          }}
          destroyOnHidden
          centered
          title={
            adminQueryStatus === 'initial'
              ? 'Добавление администратора'
              : adminPending || (!adminData && !adminError)
                ? 'Загрузка'
                : 'Редактирование администратора'
          }
          footer={null}
        >
          {formComponent}
        </Modal>
      ) : (
        formComponent
      )}
    </>
  )
}
